export const registry = {
  title: 'Container Registry',
  subtitle: 'OCI-compatible container image registry',
  storageLocation: 'Storage location',
  storageLocationValue: 'Nuremberg, Germany',

  tabs: {
    overview: 'Overview',
    tokens: 'Access Tokens',
    cleanup: 'Cleanup Rules'
  },

  notEnabled: {
    title: 'Container Registry',
    description: 'Push and pull container images with a private OCI registry. 1 GB free storage included, then €0.015/GB/month.',
    button: 'Enable Registry'
  },

  enable: {
    title: 'Enable Container Registry',
    slug: 'Registry Namespace',
    slugHint: 'Used in image paths: registry.itsh.dev/<slug>/image:tag',
    slugPlaceholder: 'my-company',
    confirm: 'Confirm',
    previewLabel: 'Image path preview',
    button: 'Enable',
    slugRequired: 'Please set a namespace first',
    slugTaken: 'This namespace is already taken',
    slugLocked: 'The namespace can no longer be changed because images are already stored under it',
    alreadyEnabled: 'This account already has a container registry',
    pricingTitle: 'Pricing',
    pricingFreeStorage: '1 GB storage included',
    pricingStorage: 'Storage above 1 GB',
    pricingStorageRate: '€0.015 / GB / month',
    pricingEgress: 'Egress',
    pricingEgressRate: '€0.005 / GB'
  },

  loadError: {
    title: 'Registry could not be loaded',
    description: 'The registry details could not be read. Please try again.',
    retry: 'Try again'
  },

  overview: {
    status: 'Status',
    created: 'Created',
    slug: 'Namespace',
    storage: 'Storage',
    freeTier: 'free',
    of: 'of',
    statuses: {
      active: 'Active',
      suspended: 'Suspended',
      deactivated: 'Deactivated',
      quota_exceeded: 'Storage full'
    },
    quotaExceededHint:
      'Your free storage allowance is used up. New pushes are blocked, pulls keep working. Delete images to get back under the limit.',
    quickstart: 'Quick Start',
    loginCommand: 'Login',
    loginHint: 'Username: {slug} — Password: a registry token',
    pushCommand: 'Push an image',
    copied: 'Copied!',
    disable: 'Disable Registry',
    disableConfirm: 'Are you sure you want to disable the container registry? Your images will remain in storage but will not be accessible.'
  },

  repos: {
    title: 'Repositories',
    empty: 'No repositories yet',
    emptyHint: 'Push your first image to get started',
    columns: {
      name: 'Repository',
      actions: 'Actions'
    },
    viewTags: 'View tags',
    delete: {
      title: 'Delete Repository',
      message: 'Are you sure you want to delete repository "{repo}" and all its images? This cannot be undone.'
    }
  },

  tags: {
    title: 'Tags',
    empty: 'No tags',
    columns: {
      tag: 'Tag',
      lastUpdated: 'Last Updated',
      actions: 'Actions'
    },
    delete: {
      title: 'Delete Tag',
      message: 'Are you sure you want to delete tag "{tag}"? This cannot be undone.'
    },
    back: 'Back to repositories'
  },

  tokens: {
    title: 'Access Tokens',
    empty: 'No access tokens yet',
    emptyHint: 'Create a token to authenticate with the registry',
    columns: {
      name: 'Name',
      scopes: 'Scopes',
      created: 'Created',
      lastUsed: 'Last Used',
      expires: 'Expires',
      actions: 'Actions'
    },
    allRepos: 'All repositories',
    never: 'Never',
    noExpiry: 'No expiry',
    pullOnly: 'Pull only',
    create: {
      title: 'Create Access Token',
      subtitle: 'Generate a token for Docker/OCI registry authentication',
      name: 'Token Name',
      namePlaceholder: 'ci-pipeline',
      scopes: 'Repository Scope',
      scopesHint:
        'Comma-separated repository names, e.g. my-app. The token can then only reach these repositories and the ones nested below them. Leave empty for access to all repositories.',
      invalidScope: 'Use repository names under your namespace, such as my-app or team/api: lowercase letters, digits, and . _ - as separators.',
      expiry: 'Expiry',
      noExpiry: 'No expiry',
      pullOnlyHint: "For clusters and deploy targets. Push and delete are refused. Can't be changed later.",
      button: 'Create Token'
    },
    secret: {
      title: 'Access Token Created',
      warning: 'Save this token now. It will not be shown again.',
      token: 'Access Token',
      copied: 'Copied!',
      tabs: {
        docker: 'Docker',
        podman: 'Podman',
        ci: 'CI/CD'
      }
    },
    delete: {
      title: 'Delete Token',
      message: 'Are you sure you want to delete token "{name}"? Any CI/CD pipelines using this token will stop working.'
    }
  },

  cleanup: {
    title: 'Cleanup Rules',
    empty: 'No cleanup rules configured',
    emptyHint: 'Add rules to automatically clean up old images and save storage costs',
    columns: {
      repository: 'Repository',
      rule: 'Rule',
      value: 'Value',
      patterns: 'Patterns',
      on: 'On',
      off: 'Off',
      enabled: 'Enabled',
      actions: 'Actions'
    },
    ruleTypes: {
      keep_last: 'Keep last N tags',
      untagged_ttl: 'Delete untagged after N days',
      tag_ttl: 'Delete tags after N days'
    },
    allRepos: 'All repositories',
    create: {
      title: 'Create Cleanup Rule',
      repository: 'Repository',
      repositoryHint: 'Use * for all repositories, or pick one from the list. A typed name is relative to your namespace',
      ruleType: 'Rule Type',
      value: 'Value',
      valueHint: {
        keep_last: '1 to 1000 tags',
        days: '1 to 3650 days'
      },
      keepPatterns: 'Keep Patterns',
      keepPatternsHint: 'Comma-separated glob patterns to always keep (e.g. v*,latest,main)',
      deletePatterns: 'Delete Patterns',
      deletePatternsHint: 'Comma-separated glob patterns to target (e.g. pr-*,sha-*)',
      button: 'Create Rule'
    },
    delete: {
      title: 'Delete Rule',
      message: 'Are you sure you want to delete this cleanup rule?'
    }
  },

  usage: {
    title: 'Usage',
    storage: 'Storage',
    egress: 'Egress',
    pulls: 'Pulls',
    pushes: 'Pushes',
    covered: 'covered by free tier',
    billable: 'billable',
    pricingHint: 'Storage: €0.015/GB/month · Egress: €0.005/GB · {freeStorage} storage + {freeEgress} egress free'
  },

  notifications: {
    enabled: 'Container registry enabled',
    disabled: 'Container registry disabled',
    tokenCreated: 'Access token created',
    tokenDeleted: 'Access token deleted',
    tagDeleted: 'Tag deleted',
    repoDeleted: 'Repository deleted',
    ruleCreated: 'Cleanup rule created',
    ruleDeleted: 'Cleanup rule deleted',
    slugSet: 'Registry namespace set'
  }
} as const
